// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import styles from './ResultsBand.module.css';
import { LineChart } from './LineChart';
import { toChartSeries, SeriesLike } from './results-series';
import {
  ChartMode,
  RESULTS_DASHES_BY_CYCLE,
  focusRecommended,
  resultsPalette,
} from './results-palette';

/**
 * The Results chart shares the band's ground, and the band flips dark under
 * [data-theme='dark'] -- so pick the palette variant that reads on that ground.
 * Impure (reads the theme attribute), so it lives in this presentation shell and
 * the palette core stays pure. Defaults to light when there's no DOM/theme.
 */
function detectChartMode(): ChartMode {
  if (typeof document !== 'undefined') {
    if (document.documentElement.getAttribute('data-theme') === 'dark') {
      return 'dark';
    }
  }
  return 'light';
}
import { BandLayout, DEFAULT_BAND_LAYOUT, toggleMaximized } from './results-band-layout';

interface ResultsBandProps {
  /** Behavior series to plot -- the model's key outputs (its stocks). */
  series: readonly SeriesLike[];
  /**
   * The band's size/mode (AC2.3). Controlled by the Editor so it survives
   * re-renders; defaults to the inline band at DEFAULT_BAND_HEIGHT. When
   * `onLayoutChange` is omitted the band is read-only (no resize/maximize
   * controls), which keeps it usable in static/embedded contexts.
   */
  layout?: BandLayout;
  /** Called with the next layout when the user drags-to-resize or maximizes. */
  onLayoutChange?: (layout: BandLayout) => void;
  /**
   * Idents that CAN be plotted (all simulated variables). CO-5 #9: when this and
   * the add/remove callbacks are supplied, the band lets the user choose which
   * variables to graph rather than only the auto-selected stocks.
   */
  availableVars?: readonly string[];
  /** Add a variable (by ident) to the plotted set. */
  onAddVar?: (ident: string) => void;
  /** Remove a variable (by ident) from the plotted set. */
  onRemoveVar?: (ident: string) => void;
}

/**
 * The Results band (bottom zone, MVP WS2 → WS8a): behavior-over-time for the
 * model's key outputs, reusing the LineChart via toChartSeries. Multiple series
 * overlay on one chart.
 *
 * WS8a: the band fills its Results zone and the chart fills the remaining height
 * (LineChart `fillHeight`). The ZoneFrame owns resize (its bottom-zone handle)
 * and maximize is a reflow -- the maximize button flips `layout.mode`, which the
 * Editor reads to grow the zone (shrinking the Model, never covering it). So the
 * band no longer carries its own resize handle or overlay; it just reports the
 * maximize toggle through `onLayoutChange`, keeping the Editor the single source
 * of truth.
 */
export class ResultsBand extends React.PureComponent<ResultsBandProps> {
  private readonly onToggleMaximize = (): void => {
    const { layout = DEFAULT_BAND_LAYOUT, onLayoutChange } = this.props;
    onLayoutChange?.(toggleMaximized(layout));
  };

  private readonly onAddVarChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const ident = e.target.value;
    if (ident) {
      this.props.onAddVar?.(ident);
    }
    // Reset back to the placeholder so the same var can be re-picked later.
    e.target.value = '';
  };

  render(): React.ReactNode {
    const { series, layout = DEFAULT_BAND_LAYOUT, onLayoutChange, availableVars, onAddVar, onRemoveVar } = this.props;
    const interactive = onLayoutChange !== undefined;
    const maximized = layout.mode === 'maximized';

    // CO-5 #9: variables the user can still add (everything simulated that isn't
    // already plotted).
    const plotted = new Set(series.map((s) => s.name));
    const addable = (availableVars ?? []).filter((v) => !plotted.has(v));
    const addControl =
      onAddVar && addable.length > 0 ? (
        <select className={styles.addVar} value="" onChange={this.onAddVarChange} aria-label="Add a variable to plot">
          <option value="">+ add variable</option>
          {addable.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      ) : null;

    const controls = interactive ? (
      <button
        type="button"
        className={styles.iconButton}
        aria-label={maximized ? 'Restore results band' : 'Maximize results band'}
        aria-pressed={maximized}
        onClick={this.onToggleMaximize}
      >
        {maximized ? '⤡' : '⤢'}
      </button>
    ) : null;

    const header = (
      <div className={styles.header}>
        <span>
          Results <span className={styles.sub}>· over time</span>
        </span>
        {focusRecommended(series.length) && (
          <span className={styles.focusHint} title="More than 7 series — line styles repeat; Focus density recommended.">
            7+ series · Focus recommended
          </span>
        )}
        {addControl}
        {controls}
      </div>
    );

    if (series.length === 0) {
      return (
        <div className={styles.band}>
          {header}
          <div className={styles.empty}>Run a simulation to see behavior over time.</div>
        </div>
      );
    }

    // WS8a locked palette: colorblind-safe 7 hues, light/dark by the band's
    // ground; past 7 series the hues repeat with a changing line style.
    const { series: chartSeries, yDomain } = toChartSeries(
      series,
      resultsPalette(detectChartMode()),
      RESULTS_DASHES_BY_CYCLE,
    );

    return (
      <div className={styles.band}>
        {header}
        <div className={styles.chart}>
          <LineChart height={0} fillHeight series={chartSeries} yDomain={yDomain} gridLines="horizontal" />
        </div>
        <div className={styles.legend}>
          {chartSeries.map((s) => (
            <span key={s.name} className={styles.legendItem}>
              <span className={styles.swatch} style={{ backgroundColor: s.color }} />
              {s.name}
              {onRemoveVar ? (
                <button
                  type="button"
                  className={styles.legendRemove}
                  aria-label={`Remove ${s.name} from the plot`}
                  onClick={() => onRemoveVar(s.name)}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
        </div>
      </div>
    );
  }
}
